// EmployeeModalV2.jsx
import "./EmployeeModal.scss";

import * as formik from "formik";
import * as yup from "yup";

import { Alert, Form as BsForm, Col, Modal, Row, Tab, Tabs } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppButton from "../../../commons/buttons/AppButton";
import AppInputV2 from "../../../commons/inputs/AppInputV2";
import AppLinkButton from "../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../commons/AppLoading";
import CompanyAPI from "../../company/CompanyAPI";
import EmployeeAPI from "../EmployeeAPI";
import HallAppToast from "../../../commons/HallAppToast";
import ServiceAPI from "../../services/ServiceAPI";
import Switch from "../../../commons/inputs/Switch";

const companyService = new CompanyAPI();
const serviceAPI = new ServiceAPI();
const employeeAPI = new EmployeeAPI();

function hasRule(employee, rule) {
  return employee?.rules?.some((r) => r?.rule === rule);
}

// helpers
const isBlank = (v) => v == null || String(v).trim() === "";
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
const toNum = (v) => {
  if (v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isNaN(n) ? null : n;
};
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

/**
 * Estrutura UI da lista de comissões:
 * {
 *   serviceId,
 *   serviceName,
 *   categoryName,
 *   serviceEmployeeId, // null => inativo
 *   percentage,        // number|null
 *   checked,           // boolean (se profissional faz)
 *   percentText,       // string (input)
 * }
 */
function mapBackendCommissionToUI(item) {
  const checked = item?.serviceEmployeeId != null; // ativas
  const percentage = item?.percentage ?? null;
  return {
    serviceId: item?.serviceId,
    serviceName: item?.serviceName || "",
    categoryName: item?.categoryName || "Outros",
    serviceEmployeeId: item?.serviceEmployeeId ?? null,
    percentage,
    checked,
    percentText: percentage == null ? "" : String(percentage).replace(".", ","),
  };
}

function EmployeeModal({ show, closeModal, employee, updateListEmployees }) {
  const [activeTab, setActiveTab] = useState("personal");

  const [isLoading, setIsLoading] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  const [isAdmin, setAdmin] = useState(false);
  const [isReception, setReception] = useState(false);

  // carregando comissões
  const [isLoadingCommissions, setIsLoadingCommissions] = useState(false);
  const [commissionItems, setCommissionItems] = useState([]); // lista flat já pronta (backend)
  const [searchTerm, setSearchTerm] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);

  // serviços (caso queira fallback no futuro)
  const [isLoadingServices, setIsLoadingServices] = useState(false);

  const isEditMode = useMemo(() => {
    return !!employee && (employee?.id != null || !!employee?.email);
  }, [employee]);

  const initialValuesDefault = useMemo(
    () => ({
      email: "",
      name: "",
      isAdmin: false,
      isReception: false,
    }),
    []
  );

  const [initialValues, setInitialValues] = useState(initialValuesDefault);

  const { Formik } = formik;

  const schema = yup.object().shape({
    email: yup.string().email("Email inválido").required("Email é obrigatório"),
    name: yup.string().nullable(),
  });

  useEffect(() => {
    // reset visual
    setActiveTab("personal");
    setShowNotification(false);
    setSearchTerm("");
    setOnlyActive(false);

    if (employee) {
      const admin = hasRule(employee, "COMPANY");
      const reception = hasRule(employee, "RECEPTION");

      setAdmin(admin);
      setReception(reception);

      setInitialValues({
        email: employee.email || "",
        name: employee?.name || "",
        isAdmin: admin,
        isReception: reception,
      });

      // ao abrir funcionário, puxa comissões
      if (employee?.id != null) {
        getCommissions(employee.id);
      } else {
        setCommissionItems([]);
      }
    } else {
      setAdmin(false);
      setReception(false);
      setInitialValues(initialValuesDefault);
      setCommissionItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, initialValuesDefault]);


  async function getCommissions(employeeId) {
    try {
      setIsLoadingCommissions(true);
      const resp = await employeeAPI.getCommissions(employeeId);

      const list = (resp?.data || []).map(mapBackendCommissionToUI);

      // ordena por categoria e nome
      list.sort((a, b) => {
        const c = String(a.categoryName || "").localeCompare(String(b.categoryName || ""));
        if (c !== 0) return c;
        return String(a.serviceName || "").localeCompare(String(b.serviceName || ""));
      });

      setCommissionItems(list);
    } catch (e) {
      setCommissionItems([]);
      // sem toast aqui pra não incomodar, mas pode ligar se quiser:
      // HallAppToast.error("Não foi possível carregar as comissões.");
    } finally {
      setIsLoadingCommissions(false);
    }
  }


  function toggleCheck(serviceId, checked) {
    setCommissionItems((prev) =>
      prev.map((it) => {
        if (it.serviceId !== serviceId) return it;
        // se desmarcar, limpa comissão visual (mas mantém pra usuário decidir depois)
        return {
          ...it,
          checked: !!checked,
          serviceEmployeeId: checked ? (it.serviceEmployeeId ?? 1) : null, // apenas flag UI; backend define de verdade
          percentText: checked ? it.percentText : it.percentText, // mantém texto
        };
      })
    );
  }

  function updatePercent(serviceId, value) {
    setCommissionItems((prev) =>
      prev.map((it) => (it.serviceId === serviceId ? { ...it, percentText: value } : it))
    );
  }

  function validateCommissionsList() {
    // valida só os marcados
    for (const it of commissionItems) {
      if (!it.checked) continue;
      if (!isBlank(it.percentText)) {
        const p = toNum(it.percentText);
        if (p == null || p < 0 || p > 100) {
          HallAppToast.error("Comissão (%) deve estar entre 0 e 100.");
          setActiveTab("commissions");
          return false;
        }
      }
    }
    return true;
  }

  const filteredAndGrouped = useMemo(() => {
    const q = norm(searchTerm);

    const filtered = (commissionItems || []).filter((it) => {
      if (onlyActive && it.serviceEmployeeId == null) return false;
      if (isBlank(q)) return true;

      const hay = norm(`${it.serviceName} ${it.categoryName}`);
      return hay.includes(q);
    });

    const groups = new Map();
    for (const it of filtered) {
      const cat = it.categoryName || "Outros";
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(it);
    }

    // transforma em array pra render e já ordena
    const arr = Array.from(groups.entries())
      .map(([categoryName, items]) => ({
        categoryName,
        items: items.slice().sort((a, b) => String(a.serviceName).localeCompare(String(b.serviceName))),
      }))
      .sort((a, b) => String(a.categoryName).localeCompare(String(b.categoryName)));

    return arr;
  }, [commissionItems, onlyActive, searchTerm]);

  const createOrUpdate = async (values, { resetForm }) => {
    if (!validateCommissionsList()) return;

    const commissionsPayload = (commissionItems || [])
      .filter((it) => it.checked)
      .map((it) => {
        const p = isBlank(it.percentText) ? null : clamp(toNum(it.percentText) ?? 0, 0, 100);
        return {
          serviceId: it.serviceId,
          percent: p, // seu backend hoje retorna "percentage" - aqui enviamos "percent" (ajuste se precisar)
        };
      });

    const data = {
      ...values,
      isAdmin: isAdmin,
      isReception: isReception,
      commissions: commissionsPayload,
    };

    setShowNotification(false);

    try {
      setIsLoading(true);

      if (isEditMode) {
        await companyService.updateEmployee(companyService.getCompanyManagment().id, data);
      } else {
        await companyService.addEmployee(companyService.getCompanyManagment().id, data);
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 6000);
        resetForm();
        setCommissionItems([]);
      }

      HallAppToast.success();
    } catch (e) {
      HallAppToast.error();
    } finally {
      setIsLoading(false);
      updateListEmployees?.();
    }
  };

  return (
    <Modal
      show={show}
      fullscreen
      centered
      onHide={() => closeModal()}
      dialogClassName="employee-modalv2__dialog"
      contentClassName="employee-modalv2__content"
    >
      <Modal.Header closeButton className="employee-modalv2__header">
        <Modal.Title className="employee-modalv2__title">
          {isEditMode ? "Alterar Funcionário" : "Adicionar Funcionário"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="employee-modalv2__body">
        <Formik validationSchema={schema} onSubmit={createOrUpdate} enableReinitialize initialValues={initialValues}>
          {({ handleSubmit, handleChange, values, touched, errors }) => (
            <div className="employee-modalv2__container">
              <BsForm noValidate onSubmit={handleSubmit}>
                <div className="employee-modalv2__tabsWrap">
                  <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="employee-modalv2__tabs">
                    <Tab eventKey="personal" title="Dados pessoais">
                      <div className="employee-modalv2__sectionCard">
                        <div className="employee-modalv2__sectionTitle">Dados do funcionário</div>

                        <Row className="g-3">
                          <Col xs={12} md={6}>
                            <AppInputV2
                              isRequired
                              disabled={isEditMode}
                              errors={errors}
                              touched={touched?.email}
                              name="email"
                              onChange={handleChange}
                              value={values.email}
                              type="email"
                              label="E-mail"
                              placeholder="ex: nome@dominio.com"
                            />
                          </Col>

                          <Col xs={12} md={6}>
                            <AppInputV2
                              disabled={isEditMode}
                              errors={errors}
                              touched={touched?.name}
                              name="name"
                              onChange={handleChange}
                              value={values.name}
                              type="text"
                              label="Nome"
                              placeholder="ex: Maria Silva"
                            />
                          </Col>
                        </Row>

                        <div className="employee-modalv2__divider" />

                        <div className="employee-modalv2__sectionTitle">Permissões</div>
                        <div className="employee-modalv2__switches">
                          <Switch label="Administrador" checked={isAdmin} onChange={(e) => setAdmin(e.target.checked)} />
                          <Switch label="Recepção" checked={isReception} onChange={(e) => setReception(e.target.checked)} />
                          <Switch label="Funcionário" checked={true} onChange={() => {}} />
                        </div>

                        <div className="employee-modalv2__hint">
                          Dica: Administrador tem acesso total. Recepção ajuda no atendimento e agenda.
                        </div>
                      </div>
                    </Tab>

                    <Tab eventKey="commissions" title="Comissões">
                      <div className="employee-modalv2__sectionCard">
                        <div className="employee-modalv2__sectionTitleRow">
                          <div className="employee-modalv2__sectionTitle">Comissões por serviço</div>
                        </div>

                        <div className="employee-modalv2__subtitle">
                          Marque os serviços que o profissional faz e informe a comissão (%). Use a busca para encontrar rápido.
                        </div>

                        <Row className="g-2 align-items-end employee-modalv2__commissionTools">
                          <Col xs={12} md={8}>
                            <AppInputV2
                              name="commissionSearch"
                              label="Buscar"
                              placeholder="Buscar por serviço ou categoria..."
                              errors={{}}
                              touched={false}
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              type="text"
                            />
                          </Col>

                          <Col xs={12} md={4}>
                            <div className="employee-modalv2__onlyActive">
                              <Switch
                                label="Somente comissões ativas"
                                checked={onlyActive}
                                onChange={(e) => setOnlyActive(e.target.checked)}
                              />
                              <div className="employee-modalv2__fieldHelp">
                                Ativas = serviço já vinculado ao funcionário (serviceEmployeeId ≠ null).
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {(isLoadingCommissions || isLoadingServices) ? (
                          <div className="employee-modalv2__loadingBox">
                            <AppLoading />
                          </div>
                        ) : (
                          <div className="employee-modalv2__commissionCategoryList">
                            {filteredAndGrouped.length === 0 ? (
                              <div className="employee-modalv2__emptyState">
                                Nenhum serviço encontrado para os filtros atuais.
                              </div>
                            ) : (
                              filteredAndGrouped.map((group) => (
                                <div key={group.categoryName} className="employee-modalv2__categoryBlock">
                                  <div className="employee-modalv2__categoryTitle">{group.categoryName}</div>

                                  <div className="employee-modalv2__categoryTable">
                                    {group.items.map((it) => (
                                      <div key={it.serviceId} className="employee-modalv2__serviceRow">
                                        <label className="employee-modalv2__serviceCheck">
                                          <input
                                            type="checkbox"
                                            checked={!!it.checked}
                                            onChange={(e) => toggleCheck(it.serviceId, e.target.checked)}
                                          />
                                          <span className="employee-modalv2__serviceName">{it.serviceName}</span>

                                          {it.serviceEmployeeId != null && (
                                            <span className="employee-modalv2__badgeActive" title="Comissão ativa">
                                              ativa
                                            </span>
                                          )}
                                        </label>

                                        <div className="employee-modalv2__servicePercent">
                                          <AppInputV2
                                            name={`percent-${it.serviceId}`}
                                            label="Comissão (%)"
                                            placeholder="ex: 10"
                                            errors={{}}
                                            touched={false}
                                            value={it.percentText}
                                            onChange={(e) => updatePercent(it.serviceId, e.target.value)}
                                            type="text"
                                            disabled={!it.checked}
                                            formText="0 a 100"
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </Tab>
                  </Tabs>
                </div>

                {isLoading ? (
                  <div className="employee-modalv2__actions">
                    <AppLoading />
                  </div>
                ) : (
                  <div className="employee-modalv2__actions">
                    <AppButton
                      type="submit"
                      width="100%"
                      name={isEditMode ? "Salvar alterações" : "Adicionar"}
                      className="mb-2"
                    />
                    <AppLinkButton width="100%" name="Fechar" onClick={closeModal} />
                  </div>
                )}
              </BsForm>

              {showNotification && (
                <div className="employee-modalv2__alertWrap">
                  <Alert key={"warning"} variant={"warning"} className="employee-modalv2__alert">
                    Convite enviado com sucesso! Avise o funcionário que ele receberá um email para ativar a conta.
                  </Alert>
                </div>
              )}
            </div>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

export default EmployeeModal;
